import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AppShell, Icon } from '../components/AppShell';
import { EmptyState } from '../components/EmptyState';
import { api, ApiError } from '../lib/api';
import { fmtEUR, arbeidskostTone, isArbeidskostAlert, ARBEIDSKOST_ALERT_PCT } from '../lib/format';
import { BouwflowLink } from '../components/BouwflowLink';
import { useToast } from '../components/Toast';

const MONTHS = ['januari','februari','maart','april','mei','juni','juli','augustus','september','oktober','november','december'];

type SortKey = 'team' | 'firma' | 'count' | 'totaal' | 'gemiddeld' | 'gemiddeldPct';
type SortDir = 'asc' | 'desc';

type MonthData = Awaited<ReturnType<typeof api.overview.month>>;
type PloegAgg = MonthData['aggregates'][number];

export function MonthOverview() {
  const now = new Date();
  const toast = useToast();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [filterPloegId, setFilterPloegId] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('totaal');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [onlyHighPct, setOnlyHighPct] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [ccMe, setCcMe] = useState(true);
  const [openPloeg, setOpenPloeg] = useState<PloegAgg | null>(null);

  // Esc sluit de projecten-popup.
  useEffect(() => {
    if (!openPloeg) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenPloeg(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPloeg]);

  const sendEmail = useMutation({
    mutationFn: (body: { year: number; month: number; ploegId: string; customMessage?: string; ccCurrentUser?: boolean }) =>
      api.overview.emailMonth(body),
    onSuccess: (resp) => {
      toast.success(`Verzonden naar ${resp.sentTo}`);
      setShowEmail(false);
      setCustomMessage('');
    },
    onError: (e) => {
      const msg = e instanceof ApiError && e.payload && typeof e.payload === 'object' && 'error' in e.payload
        ? String((e.payload as { error: string }).error)
        : 'Verzenden mislukt';
      toast.error(msg);
    },
  });

  const sendWhatsApp = useMutation({
    mutationFn: (body: { year: number; month: number; ploegId: string }) => api.overview.whatsappMonth(body),
    onSuccess: (resp) => {
      toast.success(`WhatsApp verzonden naar +${resp.sentTo}`);
    },
    onError: (e) => {
      const msg = e instanceof ApiError && e.payload && typeof e.payload === 'object' && 'error' in e.payload
        ? String((e.payload as { error: string }).error)
        : 'WhatsApp verzenden mislukt';
      toast.error(msg);
    },
  });

  const q = useQuery({
    queryKey: ['overview', year, month],
    queryFn: () => api.overview.month(year, month),
  });
  const ploegenQ = useQuery({ queryKey: ['ploegen'], queryFn: () => api.ploegen.list() });
  const allPloegen = ploegenQ.data?.ploegen ?? [];
  const selectedPloeg = filterPloegId ? allPloegen.find((p) => p.id === filterPloegId) ?? null : null;

  const data = q.data;
  const aggsRaw = (data?.aggregates ?? [])
    .filter((a) => !filterPloegId || a.ploegId === filterPloegId)
    .filter((a) => !onlyHighPct || isArbeidskostAlert(a.gemPct));
  const highPctCount = (data?.aggregates ?? []).filter((a) => isArbeidskostAlert(a.gemPct)).length;
  const grandTotal = aggsRaw.reduce((s, a) => s + (a.totaal ?? 0), 0);
  const projectCount = aggsRaw.reduce((s, a) => s + (a.count ?? 0), 0);

  const aggs = useMemo(() => {
    const sorted = [...aggsRaw];
    sorted.sort((a, b) => {
      const av = sortKey === 'team' ? a.team
        : sortKey === 'firma' ? a.firma
        : sortKey === 'count' ? a.count
        : sortKey === 'gemiddeld' ? (a.totaal ?? 0) / (a.count || 1)
        : sortKey === 'gemiddeldPct' ? (a.gemPct ?? -1)
        : a.totaal ?? 0;
      const bv = sortKey === 'team' ? b.team
        : sortKey === 'firma' ? b.firma
        : sortKey === 'count' ? b.count
        : sortKey === 'gemiddeld' ? (b.totaal ?? 0) / (b.count || 1)
        : sortKey === 'gemiddeldPct' ? (b.gemPct ?? -1)
        : b.totaal ?? 0;
      let cmp = 0;
      if (typeof av === 'string' && typeof bv === 'string') cmp = av.localeCompare(bv);
      else cmp = (av as number) - (bv as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [aggsRaw, sortKey, sortDir]);

  const maxTotal = Math.max(1, ...aggs.map((a) => a.totaal ?? 0));

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'team' || key === 'firma' ? 'asc' : 'desc');
    }
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="page-title">Maand-overzicht</h1>
          <p className="page-subtitle">Kosten per onderaannemer</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a
            href={api.overview.monthPdfUrl(year, month, filterPloegId || undefined)}
            className="btn-primary"
            download
            title={filterPloegId ? 'Detail-PDF voor deze onderaannemer met volledige break-down per project' : 'Samenvattings-PDF voor alle onderaannemers'}
          >
            <Icon name="download" size={18} />
            {filterPloegId ? 'Detail-PDF' : 'Maand-PDF'}
          </a>
          {selectedPloeg && (
            <>
              <button
                type="button"
                className="btn-accent"
                disabled={!selectedPloeg.email || projectCount === 0}
                title={
                  !selectedPloeg.email
                    ? 'Geen e-mailadres ingevuld bij deze onderaannemer'
                    : projectCount === 0
                    ? 'Geen projecten in deze periode'
                    : `Mail PDF + tekst naar ${selectedPloeg.email}`
                }
                onClick={() => setShowEmail(true)}
              >
                <Icon name="mail" size={18} />
                Doormailen
              </button>
              {selectedPloeg.telefoon && (
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={sendWhatsApp.isPending || projectCount === 0}
                  title={projectCount === 0
                    ? 'Geen projecten in deze periode'
                    : `Stuurt PDF + tekst via WhatsApp Business naar ${selectedPloeg.telefoon}`}
                  onClick={() => {
                    const periode = month === 0 ? `${year}` : `${MONTHS[month - 1]} ${year}`;
                    if (confirm(`WhatsApp-bericht versturen naar ${selectedPloeg.telefoon} (${selectedPloeg.firma}) voor ${periode}?\n\nBedrag: ${fmtEUR(grandTotal)} · ${projectCount} project${projectCount === 1 ? '' : 'en'}`)) {
                      sendWhatsApp.mutate({ year, month, ploegId: selectedPloeg.id });
                    }
                  }}
                >
                  {sendWhatsApp.isPending ? 'Bezig…' : <><Icon name="chat" size={18} />WhatsApp</>}
                </button>
              )}
            </>
          )}
          <a href={api.overview.monthCsvUrl(year, month)} className="btn-ghost" download>
            <Icon name="download" size={18} />
            CSV
          </a>
        </div>
      </div>

      <div className="card-flat mb-5 flex flex-wrap gap-x-4 gap-y-3 items-end">
        <div>
          <label htmlFor="maand-jaar" className="field-label">Jaar</label>
          <select id="maand-jaar" className="select" value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
            {[now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="maand-maand" className="field-label">Maand</label>
          <select id="maand-maand" className="select" value={month} onChange={(e) => setMonth(parseInt(e.target.value))}>
            <option value={0}>Heel jaar</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="maand-ploeg" className="field-label">Onderaannemer</label>
          <select id="maand-ploeg" className="select min-w-[220px]" value={filterPloegId} onChange={(e) => setFilterPloegId(e.target.value)}>
            <option value="">Alle onderaannemers</option>
            {allPloegen.map((p) => <option key={p.id} value={p.id}>{p.team} — {p.firma}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="maand-sorteer" className="field-label">Sorteer op</label>
          <select id="maand-sorteer" className="select" value={`${sortKey}:${sortDir}`} onChange={(e) => {
            const [k, d] = e.target.value.split(':');
            setSortKey(k as SortKey);
            setSortDir(d as SortDir);
          }}>
            <option value="totaal:desc">Totaal (hoog → laag)</option>
            <option value="totaal:asc">Totaal (laag → hoog)</option>
            <option value="count:desc">Aantal projecten (hoog)</option>
            <option value="count:asc">Aantal projecten (laag)</option>
            <option value="gemiddeld:desc">Gemiddeld per project (hoog)</option>
            <option value="gemiddeld:asc">Gemiddeld per project (laag)</option>
            <option value="gemiddeldPct:desc">Gem. % arbeidskost (hoog)</option>
            <option value="gemiddeldPct:asc">Gem. % arbeidskost (laag)</option>
            <option value="team:asc">Ploeg A → Z</option>
            <option value="firma:asc">Firma A → Z</option>
          </select>
        </div>
        <div>
          <span className="field-label">Arbeidskost</span>
          <button
            type="button"
            onClick={() => {
              const next = !onlyHighPct;
              setOnlyHighPct(next);
              // Bij inschakelen meteen sorteren op % hoog → laag, zodat de duurste bovenaan staan.
              if (next) { setSortKey('gemiddeldPct'); setSortDir('desc'); }
            }}
            aria-pressed={onlyHighPct}
            className={`inline-flex items-center gap-2 h-[38px] px-3 rounded-md border text-sm font-semibold whitespace-nowrap transition-colors ${
              onlyHighPct ? 'bg-danger text-white border-danger' : 'bg-white text-danger-700 border-danger/40 hover:bg-danger-100'
            }`}
            title={`Toon enkel onderaannemers met een gemiddelde arbeidskost boven ${ARBEIDSKOST_ALERT_PCT}%`}
          >
            <Icon name="waarschuwing" size={16} />
            Enkel &gt; {ARBEIDSKOST_ALERT_PCT}%{highPctCount > 0 ? ` (${highPctCount})` : ''}
          </button>
        </div>
        <div className="ml-auto text-right">
          <div className="text-xs font-semibold text-secondary">Totaal</div>
          <div className="text-2xl font-bold tabular-nums">{fmtEUR(grandTotal)}</div>
          <div className="text-xs text-secondary">{projectCount} project{projectCount === 1 ? '' : 'en'}</div>
        </div>
      </div>

      {aggs.length === 0 ? (
        <EmptyState
          title={onlyHighPct ? `Geen onderaannemers boven ${ARBEIDSKOST_ALERT_PCT}%` : 'Geen projecten in deze periode'}
          description={onlyHighPct ? 'Geen enkele onderaannemer overschrijdt de drempel in deze periode. Schakel de filter uit om alles te zien.' : 'Kies een andere maand of jaar, of voeg eerst projecten toe.'}
        />
      ) : (
        <>
          <div className="card mb-5">
            <h3 className="text-base font-semibold mb-1">Verdeling per ploeg</h3>
            <p className="text-xs text-secondary mb-4">Klik een onderaannemer voor de projecten van deze periode.</p>
            <div className="flex flex-col gap-1">
              {aggs.map((a) => (
                <button
                  key={a.ploegId}
                  type="button"
                  onClick={() => setOpenPloeg(a)}
                  className={`group w-full grid grid-cols-[minmax(160px,200px)_1fr_64px_150px] gap-3 items-center text-left rounded-md px-2 py-1.5 -mx-2 focus-visible:outline-none transition-colors ${
                    isArbeidskostAlert(a.gemPct) ? 'bg-danger-100 hover:brightness-95 focus-visible:brightness-95' : 'hover:bg-primary-50 focus-visible:bg-primary-50'
                  }`}
                  title={`Toon de ${a.count} project${a.count === 1 ? '' : 'en'} van ${a.team}`}
                >
                  <div className="text-sm font-medium truncate">{a.team} <span className="text-secondary font-normal">({a.firma})</span></div>
                  <div className="h-2.5 bg-lightgrey rounded-pill overflow-hidden">
                    <div className={`h-full rounded-pill transition-all ${isArbeidskostAlert(a.gemPct) ? 'bg-danger' : 'bg-primary'}`} style={{ width: `${(a.totaal / maxTotal) * 100}%` }} />
                  </div>
                  <div className="text-right"><PctBadge pct={a.gemPct} /></div>
                  <div className="text-right text-sm font-bold tabular-nums whitespace-nowrap flex items-center justify-end gap-1">
                    {fmtEUR(a.totaal)}
                    <Icon name="pijlRechts" size={16} className="text-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <h3 className="text-base font-semibold mb-3">Detail per ploeg</h3>
            <table className="w-full">
              <thead>
                <tr className="thead-row">
                  <SortTh label="Ploeg" col="team" current={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortTh label="Firma" col="firma" current={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortTh label="Projecten" col="count" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Totaal" col="totaal" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Gemiddeld" col="gemiddeld" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                  <SortTh label="Gem. %" col="gemiddeldPct" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                </tr>
              </thead>
              <tbody>
                {aggs.map((a) => (
                  <tr
                    key={a.ploegId}
                    className={`border-b border-lightgrey text-sm cursor-pointer ${isArbeidskostAlert(a.gemPct) ? 'bg-danger-100 hover:brightness-95' : 'hover:bg-primary-50'}`}
                    onClick={() => setOpenPloeg(a)}
                    title={`Toon de projecten van ${a.team}`}
                  >
                    <td className="px-3 py-2 font-semibold">
                      <span className="inline-flex items-center gap-1.5">
                        {isArbeidskostAlert(a.gemPct) && (
                          <span className="text-danger" title={`Arbeidskost boven ${ARBEIDSKOST_ALERT_PCT}%`}>
                            <Icon name="waarschuwing" size={16} />
                            <span className="sr-only">Arbeidskost boven {ARBEIDSKOST_ALERT_PCT}%</span>
                          </span>
                        )}
                        {a.team}
                      </span>
                    </td>
                    <td className="px-3 py-2"><span className="chip">{a.firma}</span></td>
                    <td className="text-right tabular-nums px-3 py-2">{a.count}</td>
                    <td className="text-right tabular-nums font-bold px-3 py-2">{fmtEUR(a.totaal)}</td>
                    <td className="text-right tabular-nums px-3 py-2">{fmtEUR((a.totaal ?? 0) / a.count)}</td>
                    <td className="text-right px-3 py-2"><PctBadge pct={a.gemPct} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className="text-right text-sm font-semibold py-3 px-3">Totaal</td>
                  <td className="text-right font-bold tabular-nums px-3">{fmtEUR(grandTotal)}</td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {showEmail && selectedPloeg && (
        <div className="fixed inset-0 bg-primary-800/40 flex items-center justify-center z-50 px-4" onClick={() => !sendEmail.isPending && setShowEmail(false)}>
          <div className="card max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-1">Maandoverzicht doormailen</h2>
            <p className="text-sm text-secondary mb-4">
              Naar <strong>{selectedPloeg.email}</strong> ({selectedPloeg.team} — {selectedPloeg.firma}).
              PDF wordt automatisch toegevoegd: <strong>{projectCount}</strong> project{projectCount === 1 ? '' : 'en'}, totaal <strong>{fmtEUR(grandTotal)}</strong>.
            </p>
            <label htmlFor="maand-bericht" className="field-label">Persoonlijk bericht (optioneel)</label>
            <textarea
              id="maand-bericht"
              className="textarea"
              rows={5}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder={`In bijlage het maandoverzicht voor ${month === 0 ? year : `${MONTHS[month - 1]} ${year}`} met de prestaties en bedragen die u kan factureren.`}
              disabled={sendEmail.isPending}
            />
            <label className="flex items-center gap-2 mt-3 text-sm">
              <input type="checkbox" checked={ccMe} onChange={(e) => setCcMe(e.target.checked)} disabled={sendEmail.isPending} />
              Mijzelf in BCC zetten
            </label>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" className="btn-ghost" onClick={() => setShowEmail(false)} disabled={sendEmail.isPending}>Annuleer</button>
              <button
                type="button"
                className="btn-accent"
                disabled={sendEmail.isPending}
                onClick={() => sendEmail.mutate({
                  year, month,
                  ploegId: selectedPloeg.id,
                  customMessage: customMessage.trim() || undefined,
                  ccCurrentUser: ccMe,
                })}
              >
                {sendEmail.isPending ? 'Verzenden…' : <><Icon name="mail" size={18} />Verzenden</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {openPloeg && (
        <div
          className="fixed inset-0 bg-primary-800/40 flex items-center justify-center z-50 px-4"
          onClick={() => setOpenPloeg(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Projecten van ${openPloeg.team}`}
        >
          <div className="card max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="min-w-0">
                <h2 className="text-lg font-bold truncate">
                  {openPloeg.team} <span className="text-secondary font-normal text-base">({openPloeg.firma})</span>
                </h2>
                <p className="text-sm text-secondary mt-0.5">
                  {month === 0 ? year : `${MONTHS[month - 1]} ${year}`} · {openPloeg.count} project{openPloeg.count === 1 ? '' : 'en'} · {fmtEUR(openPloeg.totaal)}
                </p>
              </div>
              <button type="button" className="btn-ghost btn-sm px-2" onClick={() => setOpenPloeg(null)} aria-label="Sluiten"><Icon name="sluiten" size={16} /></button>
            </div>

            <div className="flex items-center gap-2 text-sm mb-3">
              <span className="text-secondary">Gemiddelde % arbeidskost</span>
              <PctBadge pct={openPloeg.gemPct} />
              <span className="text-secondary" title="Gemiddelde over de projecten met een bekende noemer (gefactureerd of offertewaarde)">
                <Icon name="info" size={16} />
                <span className="sr-only">Gemiddelde over de projecten met een bekende noemer (gefactureerd of offertewaarde)</span>
              </span>
            </div>

            <div className="overflow-y-auto divide-y divide-lightgrey">
              {openPloeg.projects.length === 0 ? (
                <p className="text-sm text-secondary py-6 text-center">Geen projecten in deze periode.</p>
              ) : openPloeg.projects.map((pr) => (
                <div key={pr.id} className="relative flex items-center gap-3 py-2 px-2 -mx-2 rounded-md hover:bg-primary-50 transition-colors">
                  <Link
                    to={`/project/${pr.id}`}
                    className="absolute inset-0 z-[1] rounded-md"
                    aria-label={`Open calculatie ${pr.klantNaam || 'project'}`}
                    onClick={() => setOpenPloeg(null)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-accent-700 tabular-nums">
                      {pr.projectNummer ? (
                        <BouwflowLink bouwflowProjectId={pr.bouwflowProjectId} className="relative z-[2]">{pr.projectNummer}</BouwflowLink>
                      ) : '—'}
                    </div>
                    <div className="text-sm font-medium truncate">{pr.klantNaam || 'Onbekend'}</div>
                  </div>
                  <div className="text-right text-sm font-bold tabular-nums whitespace-nowrap">{fmtEUR(pr.totaal)}</div>
                  <div className="w-14 text-right"><PctBadge pct={pr.pct} voorlopig={pr.pctVoorlopig} /></div>
                  <Icon name="pijlRechts" size={16} className="text-secondary" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function PctBadge({ pct, title, voorlopig }: { pct: number | null; title?: string; voorlopig?: boolean }) {
  if (pct == null) return <span className="text-sm text-secondary tabular-nums" title={title}>—</span>;
  return (
    <span
      className={`text-sm font-bold tabular-nums ${arbeidskostTone(pct)} ${voorlopig ? 'italic underline decoration-dotted underline-offset-4' : ''}`}
      title={title ?? (voorlopig ? 'Voorlopig: bevat een regel uit een calculatie die nog niet verstuurd is' : undefined)}
    >
      {pct.toFixed(1)}%
    </span>
  );
}

interface SortThProps {
  label: string;
  col: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (col: SortKey) => void;
  align?: 'left' | 'right';
}

function SortTh({ label, col, current, dir, onSort, align = 'left' }: SortThProps) {
  const isActive = current === col;
  return (
    <th
      className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'} cursor-pointer select-none hover:text-primary ${isActive ? 'text-primary' : ''}`}
      onClick={() => onSort(col)}
      aria-sort={isActive ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {isActive && <Icon name="chevron" size={14} strokeWidth={2.25} className={dir === 'asc' ? 'rotate-180' : ''} />}
      </span>
    </th>
  );
}
